import {
  Box,
  Combobox as ChakraCombobox,
  ComboboxRootProps,
  createListCollection,
  InputGroup,
  Portal,
  Span,
  useFilter,
} from '@chakra-ui/react'
import {useTranslate} from '@tolgee/react'
import {ReactNode, useMemo, useState} from 'react'
import {LuPlus, LuSearch} from 'react-icons/lu'
import {FieldRequiredIndicator} from '../../components/form/field-required-indicator.js'
import {useFieldContext} from '../../integrations/form/index.js'
import {ErrorMessage} from './error-message.js'
import {FieldRoot} from './field-root.js'
import {SelectItem} from './select.js'

interface Props extends Omit<ComboboxRootProps, 'collection'> {
  items: SelectItem[]
  label: ReactNode
  hideLabel?: boolean
  helpText?: string
  description?: string
  placeholder?: string
  controlProps?: ChakraCombobox.ControlProps
  contentProps?: ChakraCombobox.ContentProps
  portalled?: boolean
  required?: boolean
}

export function CreatableCombobox({required, ...props}: Props) {
  const field = useFieldContext<string | string[]>()
  const {t} = useTranslate('common')
  const {contains} = useFilter({sensitivity: 'base'})

  const getInitialInputValue = () => {
    if (props.inputValue || props.defaultInputValue) {
      return props.inputValue || props.defaultInputValue || ''
    }

    const currentValue = Array.isArray(field.state.value) ? field.state.value[0] : field.state.value

    if (currentValue) {
      const selectedItem = props.items.find(item => item.value === currentValue)
      if (selectedItem) {
        return selectedItem.label
      }
    }

    return ''
  }

  const [inputValue, setInputValue] = useState(getInitialInputValue)

  const filteredItems = useMemo(() => {
    return props.items.filter(item => contains(item.label, inputValue))
  }, [props.items, contains, inputValue])

  const showCreateOption =
    Boolean(inputValue?.trim()) && !filteredItems.some(item => item.label.toLowerCase() === inputValue.toLowerCase())

  const collection = useMemo(() => {
    const items: SelectItem[] = showCreateOption
      ? [{value: inputValue.trim(), label: inputValue.trim()}, ...filteredItems]
      : filteredItems

    return createListCollection({items})
  }, [filteredItems, showCreateOption, inputValue])

  let value: string[] = []
  if (field.state.value) {
    value = Array.isArray(field.state.value) ? field.state.value : [field.state.value]
  }

  return (
    <FieldRoot required={required} disabled={props.disabled}>
      <ChakraCombobox.Root
        width="100%"
        collection={collection}
        name={field.name}
        value={value}
        allowCustomValue
        inputValue={inputValue}
        onInputValueChange={e => setInputValue(e.inputValue)}
        onValueChange={e => {
          if ('mode' in field.options && field.options.mode === 'value') {
            field.handleChange(e.value?.[0])
          } else {
            field.handleChange(e.value)
          }
        }}
        openOnClick
        onBlur={field.handleBlur}
        {...props}
        positioning={{sameWidth: true, ...props.positioning}}
      >
        <ChakraCombobox.Label srOnly={props.hideLabel} display="flex" spaceX="2">
          {props.label} {required && <FieldRequiredIndicator />}
        </ChakraCombobox.Label>

        <ChakraCombobox.Control backgroundColor="bg" {...props.controlProps}>
          <InputGroup startElement={<LuSearch />}>
            <ChakraCombobox.Input placeholder={props.placeholder} />
          </InputGroup>
          <ChakraCombobox.IndicatorGroup>
            <ChakraCombobox.ClearTrigger />
            <ChakraCombobox.Trigger />
          </ChakraCombobox.IndicatorGroup>
        </ChakraCombobox.Control>

        <Portal disabled={!props.portalled}>
          <ChakraCombobox.Positioner width="100%">
            <ChakraCombobox.Content minWidth="20rem" {...props.contentProps}>
              {collection.items?.map((item, index) => (
                <ChakraCombobox.Item key={item.value} item={item}>
                  {showCreateOption && index === 0 ? (
                    <Box display="flex" alignItems="center" gap="2">
                      <LuPlus />
                      <Span truncate>{t('create_option', {value: item.label.trim()})}</Span>
                    </Box>
                  ) : (
                    <Span truncate>{item.label}</Span>
                  )}
                  <ChakraCombobox.ItemIndicator />
                </ChakraCombobox.Item>
              ))}
            </ChakraCombobox.Content>
          </ChakraCombobox.Positioner>
        </Portal>
      </ChakraCombobox.Root>
      <ErrorMessage />
    </FieldRoot>
  )
}
